import Vue from 'vue';
import { reconcileContextRail as resolveContextRailState } from 'components/View/contextRailController';
import { defineStore } from 'pinia';

const CLOSED_CONTEXT_RAIL = {
    open: false,
    mode: 'context',
    datasetUuid: null,
    sourceViewId: null,
    ownerViewId: null
};

export const useSmartviewStore = defineStore('smartview', {
    state: () => ({
        views: [],
        browserReportCaptureViewId: null,
        selectedView: 0,
        idCounter: 0,
        isReportMode: false,
        debuggingBackToSource: false,
        cameraData: {
            cameraPos: {
                lat: null,
                lon: null,
                alt: null
            },
            clickPos: {
                lat: null,
                lon: null,
                alt: null
            },
            orientation: {
                pitch: null,
                roll: null,
                yaw: null
            },
            sourceImageUuid: null,
            sourceImageMetadata: null,
            sourceViewId: null,
            inspectionSyncPhase: null
        },
        cameraDataRevision: 0,
        syncViews: false,
        contextRail: { ...CLOSED_CONTEXT_RAIL }
    }),
    getters: {
        viewsOnPage: state => state.views,
        getViewComponent: state => viewID => {
            return state.views.find(v => v.id === viewID)?.component;
        },
        selectedViewValue: state => state.selectedView,
        selectedViewDetails: state => state.views[state.selectedView],
        getIdCounter: state => state.idCounter,
        getIsReportMode: state => state.isReportMode,
        isReportModeValue: state => state.isReportMode,
        cameraDataValue: state => state.cameraData,
        cameraDataRevisionValue: state => state.cameraDataRevision,
        debuggingBackToSourceValue: state => state.debuggingBackToSource,
        syncViewsValue: state => state.syncViews,
        contextRailValue: state => state.contextRail,
        selectedViewIsFavourite: state => {
            if (state.views[state.selectedView] !== undefined) {
                return state.views[state.selectedView].isFavourite;
            }
            return false;
        }
    },
    actions: {
        incrementIdCounter() {
            this.idCounter++;
        },
        setSyncViews(value) {
            this.syncViews = value;
        },
        setSyncedViews(value) {
            this.syncViews = value;
        },
        openContextRail(payload = {}) {
            if (
                !payload.datasetUuid ||
                payload.ownerViewId === undefined ||
                payload.ownerViewId === null
            ) {
                this.contextRail = { ...CLOSED_CONTEXT_RAIL };
                return;
            }

            this.contextRail = {
                open: true,
                mode: payload.mode === 'review' ? 'review' : 'context',
                datasetUuid: String(payload.datasetUuid),
                sourceViewId: payload.sourceViewId ?? payload.ownerViewId,
                ownerViewId: payload.ownerViewId
            };
        },
        closeContextRail() {
            this.contextRail = { ...CLOSED_CONTEXT_RAIL };
        },
        reconcileContextRail() {
            const contextRail = resolveContextRailState(
                this.views,
                this.contextRail
            );
            this.contextRail = contextRail || { ...CLOSED_CONTEXT_RAIL };
        },
        addToStartViews(view) {
            this.views.unshift(view);
            this.reconcileContextRail();
        },
        addToEndViews(view) {
            this.views.push(view);
            this.reconcileContextRail();
        },
        replaceFirstView(view) {
            if (this.views.length === 0) return;
            view.id = this.views[0].id;
            Vue.set(this.views, 0, view);
            this.reconcileContextRail();
        },
        replaceLastView(view) {
            if (this.views.length === 0) return;
            const lastIndex = this.views.length - 1;
            view.id = this.views[lastIndex].id;
            Vue.set(this.views, lastIndex, view);
            this.reconcileContextRail();
        },
        replaceViews(view) {
            const isExistingPane = this.views.includes(view);
            if (view.component && !isExistingPane) {
                const existingView = this.views.find(
                    existing => existing.component === view.component
                );
                if (existingView) {
                    view.id = existingView.id;
                }
            }
            this.views = [view];
            this.selectedView = 0;
            this.reconcileContextRail();
        },
        removeView(viewIndex) {
            const selectedViewId = this.views[this.selectedView]?.id;
            const removedViewId = this.views[viewIndex]?.id;
            this.views.splice(viewIndex, 1);
            if (this.views.length === 0) {
                this.selectedView = 0;
            } else if (selectedViewId !== removedViewId) {
                const selectedIndex = this.views.findIndex(
                    view => String(view.id) === String(selectedViewId)
                );
                this.selectedView = selectedIndex === -1 ? 0 : selectedIndex;
            } else {
                this.selectedView = Math.min(viewIndex, this.views.length - 1);
            }
            this.reconcileContextRail();
        },
        removeAllViews() {
            this.views = [];
            this.selectedView = 0;
            this.reconcileContextRail();
        },
        removeFromViews(viewID) {
            const viewIndex = this.views.findIndex(v => v.id === viewID);
            if (viewIndex !== -1) {
                this.views.splice(viewIndex, 1);
                if (this.selectedView > this.views.length - 1) {
                    this.selectedView = Math.max(this.views.length - 1, 0);
                }
                this.reconcileContextRail();
            }
        },
        setSelectedView(viewIndex) {
            this.selectedView = viewIndex;
        },
        setViewComponent(payload) {
            const [viewIndex, componentName] = payload;
            if (this.views[viewIndex]) {
                this.views[viewIndex].component = componentName;
                Vue.set(
                    this.views[viewIndex],
                    'enableReports',
                    !(
                        componentName === 'inspection_view' ||
                        componentName === 'thermal_inspection_view'
                    )
                );
            }
        },
        setViewComponentProps(payload) {
            const [viewID, props] = payload;
            const viewIndex = this.views.findIndex(v => v.id === viewID);
            if (viewIndex !== -1) {
                Vue.set(this.views[viewIndex], 'stateProps', props);
            }
        },
        setViewDatasetPackage(payload) {
            const [viewIndex, datasetPackage] = payload;
            if (this.views[viewIndex]) {
                this.views[viewIndex].datasetPackage = datasetPackage;
            }
        },
        setViewStyle(payload) {
            const [viewIndex, style] = payload;
            if (this.views[viewIndex]) {
                this.views[viewIndex].style = style;
            }
        },
        setViewIsFavourite(payload) {
            const [viewIndex, isFavourite] = payload;
            if (this.views[viewIndex]) {
                this.views[viewIndex].isFavourite = isFavourite;
            }
        },
        applyHydratedView(payload = {}) {
            const viewIndex = this.views.findIndex(
                view =>
                    view?.id === payload.viewId &&
                    view?.viewUUID === payload.expectedViewUUID
            );
            if (viewIndex === -1) {
                return null;
            }

            const view = this.views[viewIndex];
            if (
                Object.prototype.hasOwnProperty.call(payload, 'isFavourite')
            ) {
                view.isFavourite = payload.isFavourite;
            }
            if (
                Object.prototype.hasOwnProperty.call(payload, 'datasetPackage')
            ) {
                view.datasetPackage = payload.datasetPackage;
            }
            if (Object.prototype.hasOwnProperty.call(payload, 'state')) {
                view.state = payload.state;
            }
            if (Object.prototype.hasOwnProperty.call(payload, 'stateProps')) {
                view.stateProps = payload.stateProps;
            }
            if (Object.prototype.hasOwnProperty.call(payload, 'component')) {
                view.component = payload.component;
                view.enableReports = !(
                    payload.component === 'inspection_view' ||
                    payload.component === 'thermal_inspection_view'
                );
            }

            return {
                view,
                viewIndex
            };
        },
        setEnableReports(enableReports) {
            if (this.views[this.selectedView]) {
                Vue.set(
                    this.views[this.selectedView],
                    'enableReports',
                    enableReports
                );
            }
        },
        setEnableReportsView(payload) {
            const [viewId, enableReports] = payload;
            const viewIndex = this.views.findIndex(v => v.id === viewId);
            if (viewIndex !== -1) {
                Vue.set(this.views[viewIndex], 'enableReports', enableReports);
            }
        },
        setUrlParam(payload) {
            const [viewId, urlParam] = payload;
            const viewIndex = this.views.findIndex(v => v.id === viewId);
            if (viewIndex !== -1) {
                Vue.set(this.views[viewIndex], 'urlParam', urlParam);
            }
        },
        setState(payload) {
            const [viewId, state] = payload;
            const viewIndex = this.views.findIndex(v => v.id === viewId);
            if (viewIndex !== -1) {
                Vue.set(this.views[viewIndex], 'state', state);
            }
        },
        setStateProps(payload) {
            const [viewId, props] = payload;
            const viewIndex = this.views.findIndex(v => v.id === viewId);
            if (viewIndex !== -1) {
                Vue.set(this.views[viewIndex], 'stateProps', props);
            }
        },
        reverseViewsOrder() {
            const selectedViewId = this.views[this.selectedView]?.id;
            this.views.reverse();
            const selectedIndex = this.views.findIndex(
                view => String(view.id) === String(selectedViewId)
            );
            this.selectedView = selectedIndex === -1 ? 0 : selectedIndex;
            this.reconcileContextRail();
        },
        setIsReportMode(value = null) {
            if (value === null) {
                this.isReportMode = !this.isReportMode;
            } else {
                this.isReportMode = value;
            }
        },
        setReportMode(value) {
            this.isReportMode = value;
        },
        setCameraPos(value) {
            this.cameraData.cameraPos = value;
            this.cameraDataRevision += 1;
        },
        setClickPos(value) {
            this.cameraData.clickPos = value;
            this.cameraDataRevision += 1;
        },
        setOrientation(value) {
            this.cameraData.orientation = value;
            this.cameraDataRevision += 1;
        },
        setSourceImageUuid(value) {
            this.cameraData.sourceImageUuid = value;
            this.cameraDataRevision += 1;
        },
        setSourceViewId(value) {
            this.cameraData.sourceViewId = value;
            this.cameraDataRevision += 1;
        },
        setDebuggingBackToSource(value) {
            this.debuggingBackToSource = value;
        },
        updateCamera(payload) {
            this.cameraData.cameraPos = {
                lat: payload.camera_pos.lat,
                lon: payload.camera_pos.lon,
                alt: payload.camera_pos.alt
            };

            if ('click_pos' in payload) {
                if (payload.click_pos) {
                    this.cameraData.clickPos = {
                        lat: payload.click_pos.lat,
                        lon: payload.click_pos.lon,
                        alt: payload.click_pos.alt
                    };
                } else {
                    this.cameraData.clickPos = {
                        lat: null,
                        lon: null,
                        alt: null
                    };
                }
            }

            this.cameraData.orientation = {
                pitch: payload.orientation.pitch,
                roll: payload.orientation.roll,
                yaw: payload.orientation.yaw
            };

            if ('uuid' in payload) {
                this.cameraData.sourceImageUuid = payload.uuid;
            } else {
                this.cameraData.sourceImageUuid = null;
            }

            if ('source_image_metadata' in payload) {
                this.cameraData.sourceImageMetadata =
                    payload.source_image_metadata || null;
            } else {
                this.cameraData.sourceImageMetadata = null;
            }

            if ('source_view_id' in payload) {
                this.cameraData.sourceViewId = payload.source_view_id;
            } else {
                this.cameraData.sourceViewId = null;
            }
            this.cameraData.inspectionSyncPhase =
                payload.inspection_sync_phase || null;
            this.cameraDataRevision += 1;
        },
        updateClickPos(payload) {
            this.cameraData.clickPos = {
                lat: payload.pos.lat,
                lon: payload.pos.lon,
                alt: payload.pos.alt
            };
            this.cameraDataRevision += 1;
        },
        exportCSV() {
            // Intentionally a no-op placeholder for legacy callers.
        }
    }
});
